/*
 * Copyright (C) 2018-2019 Nettoken Ltd. All rights reserved.
 *
 * This document is the property of Nettoken Ltd.
 * It is considered confidential and proprietary.
 *
 * This document may not be reproduced or transmitted in any form,
 * in whole or in part, without the express written permission of
 * Nettoken Ltd.
 */
import _sodium from 'libsodium-wrappers';
import { isE2ETest, isTest } from '@nettoken/env';

import {
  CW_CONVERT_INPUT,
  CW_DECRYPT_CREDENTIALS,
  CW_DECRYPT_DASHBOARD,  
  CW_DECRYPT_MASTER_SECRET_KEY,
  CW_DECRYPT_WITH_EXTERNAL_KEYS,
  CW_DERIVE_PUBLIC_KEY_FROM_SECRET,
  CW_ENCRYPT_CREDENTIALS,
  CW_ENCRYPT_MASTER_SECRET_KEY,
  CW_ENCRYPT_WITH_EXTERNAL_KEY,
  CW_EXTRACT_MASTER_KEYS,
  CW_EXTRACT_SESSION_KEYS,
  CW_GENERATE_EPHEMERAL_KEY_PAIR,
  CW_GET_EXTERNAL_PUBLIC_KEY,
  CW_QR_AUTH_GET,
  CW_QR_AUTH_RESET,
  CW_QR_AUTH_SET,
  CW_RESET_KEYS,
  CW_SET_SESSION_KEYS,
  CW_ENCRYPT_USING_MASTER_KEYPAIR,
  CW_DECRYPT_USING_MASTER_KEYPAIR,
} from './const';

import handlers from './handlers';
import KeyPair from './keypair';
import cryptoPolyfill from './polyfill';

// Mock Worker interface to have a fallback for environments that do not
// support Workers. Such environments should import this module as usually
// and call the exposed methods to achieve the same functionality. We have
// a worker utility module that handles these things for us.
const exported = {};

// This is used to encrypt other keypair values when we want to extract them.
// We extract values when we want to sync state between extension and web app.
const externalKeyPair = new KeyPair();

// This is used to perform cryptographic functions on the user's account data.
// For example, decrypting accounts on load or encrypting them before sending
// them to the server.
const masterKeyPair = new KeyPair();

// This is used to securely receive sensitive information from the mobile app
// or other sources. We pass the source our public key which will be used to
// encrypt the secret information. Only we can decrypt it afterwards because
// we have the matching private key.
const sessionKeyPair = new KeyPair();

// Libsodium does not become available right after we import it. While it
// initialises, we queue actions that will be executed as soon as libsoidum
// becomes ready.
let queuedMessages = [];

let b64variant;
let handler;
let sodium;

/**
 * Do not use postMessage() in test. It would throw an error saying it needs
 * 2 arguments.
 *
 * @param {object} data Payload for the caller script.
 */
const broadcastResponse = data => {
  try {
    postMessage(data);
  }
  catch (e) {
    if (typeof e === 'object' &&
      e.message.includes('requires 2 arguments') &&
      typeof exported.onmessage === 'function') {
      exported.onmessage({ data });
    }
  }
};

/**
 * Ensure all required variables are defined before
 * we can proceed with calling handler methods.
 */
const checkRequiredVariables = () => {
  if (!b64variant) b64variant = sodium.base64_variants.ORIGINAL;
  if (!handler) {
    handler = handlers(
      sodium,
      b64variant,
      masterKeyPair,
      sessionKeyPair,
      externalKeyPair); // eslint-disable-line function-paren-newline
  }
};

/**
 * Add the message to the queue. This method is called only while libsodium
 * is not ready yet.
 *
 * @param {MessageEvent} event
 */
const queueMessage = event => {
  queuedMessages = [
    ...queuedMessages,
    event,
  ];
};

/**
 * @param {object} payload
 */
const handleMessage = async payload => {
  if (!sodium) {
    queueMessage(payload);
    return;
  }

  checkRequiredVariables();

  const { data } = payload;
  const { event } = data;

  const args = Object.assign({}, data);
  delete args.event;

  const unused = Object.assign({}, args);
  let response;

  switch (event) {
    case CW_ENCRYPT_USING_MASTER_KEYPAIR:
      delete unused.message;
      response = await handler.encryptUsingMasterKeyPair(args.message, 'plain', 'base64');
      break;

    case CW_DECRYPT_USING_MASTER_KEYPAIR:
      delete unused.message;
      response = await handler.decryptUsingMasterKeyPair(args.message, 'plain', 'base64');
      break;

    case CW_CONVERT_INPUT:
      delete unused.input;
      delete unused.inputFormat;
      delete unused.outputFormat;
      response = await handler.convertInput(args.input, args.inputFormat, args.outputFormat);
      break;

    case CW_DECRYPT_CREDENTIALS:
      delete unused.credentials;
      response = await handler.decryptCredentials(args.credentials);
      break;

    case CW_DECRYPT_DASHBOARD:
      response = await handler.decryptDashboards(args.dashboard);
      break;    

    case CW_DECRYPT_MASTER_SECRET_KEY:
      delete unused.encrypted;
      delete unused.publicKey;
      response = await handler.decryptMasterSecretKey(args.encrypted, args.publicKey);
      break;

    case CW_DECRYPT_WITH_EXTERNAL_KEYS:
      delete unused.encrypted;
      delete unused.format;
      delete unused.publicKey;
      delete unused.secretKey;
      response = await handler.decryptWithExternalKeys(
        args.encrypted,
        args.format,
        args.publicKey,
        args.secretKey); // eslint-disable-line function-paren-newline
      break;

    case CW_DERIVE_PUBLIC_KEY_FROM_SECRET:
      delete unused.secretKey;
      response = await handler.derivePublicKeyFromSecret(args.secretKey);
      break;

    case CW_ENCRYPT_CREDENTIALS:
      delete unused.credentials;
      response = await handler.encryptCredentials(args.credentials);
      break;

    case CW_ENCRYPT_MASTER_SECRET_KEY:
      delete unused.secretKey;
      response = await handler.encryptMasterSecretKey(args.secretKey);
      break;

    case CW_ENCRYPT_WITH_EXTERNAL_KEY:
      delete unused.inputFormat;
      delete unused.message;
      delete unused.outputFormat;
      delete unused.publicKey;
      response = await handler.encryptWithExternalKey(
        args.message,
        args.publicKey,
        args.inputFormat,
        args.outputFormat); // eslint-disable-line function-paren-newline
      break;

    case CW_EXTRACT_MASTER_KEYS:
      delete unused.publicKey;
      delete unused.secretKey;
      response = await handler.extractMasterKeys(args.publicKey, args.secretKey);
      break;

    case CW_EXTRACT_SESSION_KEYS:
      delete unused.publicKey;
      response = await handler.extractSessionKeys(args.publicKey);
      break;

    case CW_GENERATE_EPHEMERAL_KEY_PAIR:
      delete unused.format;
      response = await handler.generateEphemeralKeyPair(args.format);
      break;

    case CW_GET_EXTERNAL_PUBLIC_KEY:
      response = await handler.getExternalPublicKey();
      break;

    case CW_QR_AUTH_GET:
      response = await handler.getAuthString();
      break;

    case CW_QR_AUTH_RESET:
      response = await handler.resetAuthString();
      break;

    case CW_QR_AUTH_SET:
      delete unused.sid;
      delete unused.mock;
      response = await handler.setAuthString(args.sid, args.mock);
      break;

    case CW_RESET_KEYS:
      response = await handler.resetKeys();
      break;

    case CW_SET_SESSION_KEYS:
      delete unused.publicKey;
      delete unused.secretKey;
      response = await handler.setSessionKeys(args.publicKey, args.secretKey);
      break;

    // no default
  }

  broadcastResponse(Object.assign({}, unused, response, { event }));
};

// Implement `addEventListener()` method for unsupported environments.
addEventListener('message', handleMessage); // eslint-disable-line no-restricted-globals
exported.postMessage = data => handleMessage({ data });

/**
 * Import libsodium. We need to wait until it is ready to perform operations.
 * Once we receive the signal, flush the queued actions. There will not be
 * any more queued actions afterwards as they will get executed right away
 * when libsodium is ready.
 */
const init = async () => {
  try {
    if (!isTest || isE2ETest) cryptoPolyfill();
    await _sodium.ready;
    sodium = _sodium;
    queuedMessages.forEach(handleMessage);
    queuedMessages = [];
  }
  catch (e) {
    console.log(e);
  }
};

init();

export default () => exported;
